import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import AccountsBcpDetail from './AccountsBcpDetail';
import { fmtMoney, accountKey, fmtFyStart } from './accountsFormat';
import './AccountsBcpsTab.css';

/*
 * AccountsBcpsTab — per-agency Budget Change Proposals (P5b port of the
 * GAS BcpsPanel anatomy): KPI stat row, business-priority theme bars,
 * and a read-only request list with expandable AI-relevance detail.
 * Data: GET /api/v1/bcps?dept=<acronym>&limit=500 (deduped grain —
 * latest submission per request). Analysis is pre-generated offline;
 * un-analyzed BCPs degrade honestly to "—" tiers.
 */

export const BCP_TIER_LABEL = { high: 'High', medium: 'Med', low: 'Low', none: 'None' };
const BCP_THEME_LABEL = {
  workforce_staffing: 'Workforce', it_modernization: 'IT modernization',
  cybersecurity: 'Cybersecurity', facilities_capital: 'Facilities',
  public_safety_justice: 'Public safety', health_human_services: 'Health & human svcs',
  environment_climate: 'Environment', transportation_infrastructure: 'Transportation',
  compliance_mandate: 'Compliance mandate', grants_local_assistance: 'Grants / local',
  revenue_tax_ops: 'Revenue & tax ops', education_workforce_dev: 'Education',
  admin_operations: 'Admin ops', other: 'Other',
};

const bcpIsIt = (b) => b.itCheckbox === true || b.isItRelated === true;
// Tech-relevant: deep-analyzed → any tier above 'none'; otherwise the
// DF-46 IT checkbox or the priorities-lite tech-adjacency flag.
const bcpTechRelevant = (b) =>
  (b.panwTier ? b.panwTier !== 'none' : (bcpIsIt(b) || b.techAdjacent === true));

export function BcpTierChip({ tier, score }) {
  if (!tier) {
    return <span className="bcp-tier bcp-tier--pending" title="Not yet analyzed">—</span>;
  }
  return (
    <span
      className={`bcp-tier bcp-tier--${tier}`}
      title={score != null ? `PANW relevance ${score}/100` : undefined}
    >
      {BCP_TIER_LABEL[tier] || tier}
    </span>
  );
}

export function BcpThemeTag({ theme }) {
  if (!theme) return null;
  return <span className="bcp-theme-tag">{BCP_THEME_LABEL[theme] || theme}</span>;
}

function BcpKpis({ bcps }) {
  const tech = bcps.filter(bcpTechRelevant);
  const techAsk = tech.reduce((s, b) => s + (Number(b.requestAmount) || 0), 0);
  const tiers = { high: 0, medium: 0, low: 0, none: 0, pending: 0 };
  bcps.forEach((b) => { tiers[b.panwTier || 'pending'] += 1; });
  const analyzed = bcps.length - tiers.pending;
  const stageReady = bcps.filter((b) => /S3|S4/i.test(b.itApprovalDocument || '')).length;
  return (
    <div className="ts-stat-row">
      <div className="ts-stat-card">
        <div className="ts-stat-card__value">{tech.length}</div>
        <div className="ts-stat-card__label">Tech-relevant BCPs · {fmtMoney(techAsk)} ask</div>
      </div>
      <div className="ts-stat-card">
        <div className="ts-stat-card__value">{tiers.high}</div>
        <div className="ts-stat-card__label">High relevance · {tiers.medium} med · {tiers.low} low</div>
      </div>
      <div className="ts-stat-card">
        <div className="ts-stat-card__value">{stageReady}</div>
        <div className="ts-stat-card__label">Procurement-ready (S3/S4)</div>
      </div>
      <div className="ts-stat-card">
        <div className="ts-stat-card__value">{analyzed}<span className="bcp-kpi-unit"> / {bcps.length}</span></div>
        <div className="ts-stat-card__label">AI-analyzed</div>
      </div>
    </div>
  );
}

function BcpThemes({ bcps, themeFilter, onToggle }) {
  const rows = useMemo(() => {
    const agg = {};
    bcps.forEach((b) => {
      if (!b.primaryTheme) return;
      if (!agg[b.primaryTheme]) agg[b.primaryTheme] = { usd: 0, n: 0 };
      agg[b.primaryTheme].usd += Number(b.requestAmount) || 0;
      agg[b.primaryTheme].n += 1;
    });
    return Object.entries(agg).map(([t, v]) => ({ t, ...v }))
      .sort((x, y) => y.usd - x.usd).slice(0, 8);
  }, [bcps]);
  if (rows.length === 0) return null;
  const max = Math.max(...rows.map((r) => r.usd), 1);
  return (
    <div className="ts-card">
      <div className="accounts-card-head">
        <h3 className="ts-card-title">Business priorities</h3>
        <span className="bcp-note">where this agency asks for money — all BCPs, tech or not</span>
      </div>
      <div className="bcp-themes">
        {rows.map((r) => (
          <button
            type="button"
            className={`bcp-theme-row${themeFilter === r.t ? ' is-active' : ''}`}
            key={r.t}
            onClick={() => onToggle(r.t)}
            title={`${BCP_THEME_LABEL[r.t] || r.t}: ${r.n} BCP(s)`}
          >
            <span className="bcp-theme-label">{BCP_THEME_LABEL[r.t] || r.t}</span>
            <span className="bcp-theme-track">
              <span className="bcp-theme-fill" style={{ width: `${(r.usd / max) * 100}%` }} />
            </span>
            <span className="bcp-theme-value">{fmtMoney(r.usd)} · {r.n}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AccountsBcpsTab({ selectedDepartment }) {
  const account = accountKey(selectedDepartment);
  const [view, setView] = useState('tech'); // 'tech' | 'all'
  const [themeFilter, setThemeFilter] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['accounts-bcps', account],
    queryFn: () => apiClient.get('/api/v1/bcps', { params: { dept: account, limit: 500 } }),
    enabled: !!account,
    staleTime: 30 * 60 * 1000,
  });

  if (isLoading) return <div className="accounts-panel-note">Loading budget change proposals…</div>;
  if (error) {
    return (
      <div className="accounts-error">
        <AlertCircle size={16} /> {error.message || 'Failed to load BCPs'}
      </div>
    );
  }

  const bcps = data?.bcps || [];
  if (bcps.length === 0) {
    return (
      <div className="ts-card">
        <p className="accounts-panel-note">No BCPs analyzed for this agency yet.</p>
      </div>
    );
  }

  const tierRank = { high: 4, medium: 3, low: 2, none: 1 };
  const list = (view === 'tech' ? bcps.filter(bcpTechRelevant) : bcps.slice())
    .filter((b) => (themeFilter === null ? true : b.primaryTheme === themeFilter))
    .sort((a, b) =>
      ((b.panwTier ? tierRank[b.panwTier] : 0) - (a.panwTier ? tierRank[a.panwTier] : 0)) ||
      ((b.panwScore ?? -1) - (a.panwScore ?? -1)) ||
      ((Number(b.requestAmount) || 0) - (Number(a.requestAmount) || 0)));

  const toggleTheme = (t) => {
    if (themeFilter !== t) setView('all'); // priorities span non-tech BCPs too
    setThemeFilter(themeFilter === t ? null : t);
  };

  return (
    <div className="accounts-bcps">
      <BcpKpis bcps={bcps} />
      <BcpThemes bcps={bcps} themeFilter={themeFilter} onToggle={toggleTheme} />

      <div className="ts-card">
        <div className="accounts-card-head">
          <h3 className="ts-card-title">
            {view === 'tech' ? 'Tech-relevant requests' : 'All requests'}
            {themeFilter ? ` · ${BCP_THEME_LABEL[themeFilter] || themeFilter}` : ''}
          </h3>
          <div className="bcp-toggle" role="group" aria-label="Request view">
            <button type="button" className={`bcp-pill${view === 'tech' ? ' is-active' : ''}`} onClick={() => setView('tech')}>Tech</button>
            <button type="button" className={`bcp-pill${view === 'all' ? ' is-active' : ''}`} onClick={() => setView('all')}>All</button>
          </div>
        </div>
        <p className="bcp-note">AI-evaluated · latest submission per request · all fiscal years · source: DOF</p>

        {list.length === 0 && <p className="accounts-panel-note">No matching requests.</p>}
        <div className="bcp-list">
          {list.slice(0, 75).map((b) => {
            const open = expanded === b.bcpId;
            const meta = [
              b.canonicalNumber, b.periodLabel, fmtFyStart(b.fyStartYear),
              b.positions ? `${Number(b.positions).toFixed(0)} positions` : null,
            ].filter(Boolean).join(' · ');
            return (
              <div className={`bcp-row-wrap${open ? ' is-open' : ''}`} key={b.bcpId}>
                <button
                  type="button"
                  className="bcp-row"
                  onClick={() => setExpanded(open ? null : b.bcpId)}
                  aria-expanded={open}
                >
                  <span className="bcp-row-main">
                    <span className="bcp-row-title">{b.title}</span>
                    <span className="bcp-row-meta">{meta} <BcpThemeTag theme={b.primaryTheme} /></span>
                  </span>
                  <BcpTierChip tier={b.panwTier} score={b.panwScore} />
                  <span className="bcp-row-ask">{b.requestAmount ? fmtMoney(Number(b.requestAmount)) : '—'}</span>
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {open && <AccountsBcpDetail bcp={b} />}
              </div>
            );
          })}
        </div>
        {list.length > 75 && <p className="bcp-note">Showing top 75 of {list.length}.</p>}
      </div>
    </div>
  );
}
